'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSession } from 'next-auth/react';

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { status } = useSession();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (status !== 'authenticated') {
      setItems([]);
      setError(false);
      return;
    }
    setLoading(true);
    try {
      const r = await fetch('/api/favorites', { cache: 'no-store' });
      if (r.ok) {
        const d = await r.json();
        setItems(Array.isArray(d.items) ? d.items : []);
        setError(false);
      } else {
        setItems([]);
        setError(true);
      }
    } catch {
      setItems([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const isFavorite = useCallback(
    (slug, templateId) =>
      items.some((i) => i.destinationSlug === slug && i.templateId === templateId),
    [items],
  );

  const findFavoriteId = useCallback(
    (slug, templateId) =>
      items.find((i) => i.destinationSlug === slug && i.templateId === templateId)?.id ?? null,
    [items],
  );

  const addFavorite = useCallback(
    async ({ destinationSlug, templateId, labelSnapshot, thumbnailUrl }) => {
      const r = await fetch('/api/favorites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destinationSlug,
          templateId,
          labelSnapshot,
          thumbnailUrl,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = [d?.error, d?.detail].filter(Boolean).join(' — ') || 'Грешка при запазване';
        throw new Error(msg);
      }
      if (d.item) {
        setItems((prev) => {
          const filtered = prev.filter(
            (p) =>
              !(p.destinationSlug === d.item.destinationSlug && p.templateId === d.item.templateId),
          );
          return [d.item, ...filtered];
        });
      }
      return d.item;
    },
    [],
  );

  const removeFavoriteById = useCallback(async (id) => {
    const r = await fetch(`/api/favorites/${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      const msg = [d?.error, d?.detail].filter(Boolean).join(' — ') || 'Грешка при премахване';
      throw new Error(msg);
    }
    setItems((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const removeFavoriteBySlugTemplateId = useCallback(
    async (destinationSlug, templateId) => {
      const id = findFavoriteId(destinationSlug, templateId);
      if (!id) {
        return;
      }
      await removeFavoriteById(id);
    },
    [findFavoriteId, removeFavoriteById],
  );

  const toggleFavorite = useCallback(
    async ({ destinationSlug, templateId, labelSnapshot, thumbnailUrl }) => {
      if (isFavorite(destinationSlug, templateId)) {
        await removeFavoriteBySlugTemplateId(destinationSlug, templateId);
        return false;
      }
      await addFavorite({ destinationSlug, templateId, labelSnapshot, thumbnailUrl });
      return true;
    },
    [addFavorite, isFavorite, removeFavoriteBySlugTemplateId],
  );

  const value = useMemo(
    () => ({
      items,
      loading,
      error,
      refresh: load,
      isFavorite,
      findFavoriteId,
      toggleFavorite,
      removeFavoriteById,
    }),
    [items, loading, error, load, isFavorite, findFavoriteId, toggleFavorite, removeFavoriteById],
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) {
    throw new Error('useFavorites must be used within FavoritesProvider');
  }
  return ctx;
}

export function useFavoritesOptional() {
  return useContext(FavoritesContext);
}
